import { palette, shades } from "lib/miolo";
import TextTooltip from "components/shared/TextTooltip";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { orderBy } from "lodash";
import tinycolor from "tinycolor2";

interface IPropertyPlantEquipmentListProps {
  propertyPlantEquipment: IBrazilianCompanyIssuerTopLevelFragment["propertyPlantEquipment"];
}

export default function PropertyPlantEquipmentList({ propertyPlantEquipment }: IPropertyPlantEquipmentListProps) {
  return (
    <div
      css={{
        padding: 8,
        overflow: "hidden",
        backgroundColor: palette.white,
        borderRadius: 4,
      }}
    >
      <table
        css={{
          minWidth: "100%",
          fontSize: 14,
          fontFamily: "museo-sans-condensed",
          lineHeight: 1.33,
          letterSpacing: 0.6,
          textAlign: "left",
          textIndent: 0,
          borderColor: "inherit",
          borderCollapse: "collapse",
          "& > thead": {
            backgroundColor: palette.white,
          },
          "& th": {
            padding: "12px 16px",
            color: shades.greys.variants[2],
            fontWeight: 600,
            textTransform: "uppercase",
          },
          "& tr > td": {
            padding: "12px 16px",
          },
          "& > tbody": {
            "& tr": {
              borderColor: palette.fiord,
              borderTopWidth: 1,
              "&:hover": {
                backgroundColor: tinycolor(palette.catskillWhite).lighten(2).toHexString(),
                "& td:first-of-type": {
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                },
                "& td:last-of-type": {
                  borderTopRightRadius: 6,
                  borderBottomRightRadius: 6,
                },
              },
            },
          },
          "& tbody > tr": {
            borderTop: `solid 1px ${tinycolor(palette.mystic).lighten(3).toHexString()}`,
            borderBottom: "none",
            "&:hover, &:hover + tr": {
              borderTop: "solid 1px transparent",
            },
          },
        }}
      >
        <thead>
          <tr>
            <th>Descrição do Ativo / País</th>
            <th>Tipo de Propriedade</th>
          </tr>
        </thead>

        <tbody>
          {orderBy(propertyPlantEquipment, ["type", "country"], ["asc", "asc"]).map((node, index) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${node.description}/${index}`}>
                <td>
                  <div>
                    <span
                      css={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: shades.greys.variants[2],
                      }}
                    >
                      {node.description}
                    </span>
                  </div>

                  <div>
                    <span css={{ fontSize: 14, color: shades.greys.variants[3], letterSpacing: 1 }}>
                      {[node.country, node.city].filter(notUndefinedOrNull).join(", ")}
                    </span>
                  </div>
                </td>

                <td>{node.typeOfProperty}</td>

                <td css={{ textAlign: "right" }}>
                  {node.geographicalCountry && (
                    <div>
                      <TextTooltip
                        key={node.geographicalCountry.isoCode}
                        id={`${node.description}/${node.geographicalCountry.isoCode}`}
                        placement="top"
                        content={node.geographicalCountry.name}
                      >
                        {({ accessibility, bind, ref }) => (
                          <img
                            {...accessibility}
                            {...bind}
                            ref={ref}
                            title={node.geographicalCountry?.name}
                            alt={node.geographicalCountry?.name}
                            src={node.geographicalCountry?.flagSvg.ovalHref}
                            width={24}
                            height={24}
                            css={{
                              width: 24,
                              height: 24,
                            }}
                          />
                        )}
                      </TextTooltip>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
